import { Lock } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'

import { ActionInfoQuiet, Button, formatDayFull } from '@krp/ui-kit'

import type { CalendarDay } from '../model/calendar'
import { cellKey, type CellKind, type GridCell, type GridRow } from '../model/grid'
import type { DeniedCell } from '../model/types'
import { CellMenu } from './CellMenu'
import { swatchStyle } from './cssVars'
import './schedule.css'

const MOVES: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
}

export interface ScheduleGridProps {
  /** Название таблицы для диктора: «Кассиры объекта по дням, Октябрь 2026». */
  caption: string
  /** Подпись первого столбца: «Сотрудник» или «Объект». */
  cornerLabel: string
  days: readonly CalendarDay[]
  rows: readonly GridRow[]
  /** Клетки — кнопки; без права править они только показывают отметки. */
  editable: boolean
  refreshing: boolean
  /**
   * Ключ прокрутки: пока он тот же, таблица не прокручивается сама (сохранение клетки, перечитывание); сменился — месяц или
   * кассир другие, и колонка дат снова ставится на сегодняшний день или в начало.
   */
  scrollKey: string
  /** Кнопки в подписи строки (убрать кассира из состава). */
  rowTools?: (row: GridRow) => ReactNode
  /** Клетка с отказом «день занят»: вспыхивает красным, рядом подсказка с текстом. */
  denied: DeniedCell | null
  /** Левый клик: «выходит» ставится, если клетка не «выходит», и очищается, если выходит. */
  onToggle: (row: GridRow, cell: GridCell) => void
  /** Выбор статуса в контекстном меню клетки (`none` — «Очистить»). */
  onSetKind: (row: GridRow, cell: GridCell, kind: CellKind) => void
  /** Убрать подсказку: курсор ушёл с клетки, фокус потерян, нажали Esc или другое место. */
  onDismissDenied: () => void
}

const TIP_GAP = 6
const TIP_MARGIN = 8
/** Долгое нажатие пальцем открывает меню клетки; сдвиг дальше этого расстояния — это прокрутка, а не нажатие. */
const LONG_PRESS_MS = 500
const PRESS_SLOP = 8

/** Открытое меню: клетка (строка и день, а не снимок — вид берётся из актуальных строк) и точка, у которой оно открыто. */
interface MenuState {
  rowKey: string
  day: string
  x: number
  y: number
}

/** Чётность отказа: смена значения перезапускает анимацию вспышки на той же кнопке, не пересоздавая её (фокус остаётся). */
const deniedMark = (denied: DeniedCell | null, key: string): 'even' | 'odd' | undefined =>
  denied === null || denied.key !== key ? undefined : denied.seq % 2 === 0 ? 'even' : 'odd'

/** Курсор ушёл или фокус потерян у клетки с отказом: подсказка не нужна. */
function dismissOn(denied: DeniedCell | null, key: string, dismiss: () => void) {
  if (denied?.key === key) dismiss()
}

const cellElement = (root: HTMLElement, key: string): HTMLElement | undefined =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-cell]')).find((element) => element.dataset.cell === key)

/**
 * Ставит подсказку у клетки: она `fixed`, чтобы её не обрезала прокрутка таблицы, и не выходит за края окна. Над клеткой, а у верхнего
 * края окна — под ней. Позицию задаёт сам элемент (без состояния): она нужна только пока подсказка видна.
 */
function placeTip(tip: HTMLElement, cell: HTMLElement) {
  const rect = cell.getBoundingClientRect()
  const left = rect.left + rect.width / 2 - tip.offsetWidth / 2
  const above = rect.top - tip.offsetHeight - TIP_GAP
  tip.style.left = `${Math.max(TIP_MARGIN, Math.min(left, window.innerWidth - tip.offsetWidth - TIP_MARGIN))}px`
  tip.style.top = `${above >= TIP_MARGIN ? above : rect.bottom + TIP_GAP}px`
}

/**
 * Содержимое клетки: вид показан буквой, а не только цветом заливки — «Р» (выходит), «В» (выходной день), «Н» (не выходит,
 * отмечено) и приглушённая «Н» (не отмечено); пока команда не подтверждена — круговой загрузчик. Не отмеченный день, занятый на
 * другом объекте, выглядит как «Н»: замок лежит рядом и показывается только при наведении или фокусе (стиль `.sch-tbl__lock`),
 * буква в этот момент скрыта. Подпись для диктора лежит на самой клетке.
 */
function CellMark({ cell }: { cell: GridCell }) {
  if (cell.saving) return <span className="sch-spin" aria-hidden="true" />
  if (cell.kind === 'work') return <span aria-hidden="true">Р</span>
  if (cell.kind === 'day_off') return <span aria-hidden="true">В</span>
  return (
    <span aria-hidden="true" className={cell.kind === 'absent' ? 'sch-tbl__off sch-tbl__off--marked' : 'sch-tbl__off'}>
      <span className="sch-tbl__letter">Н</span>
      {cell.kind === 'none' && cell.busyAt !== null && <Lock size={12} className="sch-tbl__lock" />}
    </span>
  )
}

/**
 * Ставит колонку дат так, чтобы сегодняшний день был виден (по возможности по центру области дат между закреплёнными
 * колонками); не текущий месяц — начало. Страница по вертикали не движется (`scrollTo` у самого контейнера, а не
 * `scrollIntoView`), анимации нет.
 */
function scrollToToday(element: HTMLElement) {
  if (typeof element.scrollTo !== 'function') return
  const today = element.querySelector<HTMLElement>('thead th[data-today]')
  if (today === null) {
    element.scrollTo({ left: 0 })
    return
  }
  const fixedLeft = element.querySelector<HTMLElement>('thead .sch-tbl__who')?.offsetWidth ?? 0
  const fixedRight = element.querySelector<HTMLElement>('thead .sch-tbl__total')?.offsetWidth ?? 0
  const visible = element.clientWidth - fixedLeft - fixedRight
  element.scrollTo({ left: Math.max(today.offsetLeft - fixedLeft - (visible - today.offsetWidth) / 2, 0) })
}

/**
 * Таблица графика месяца: слева «Сотрудник» (или «Объект»), сверху дни — число и под ним день недели, справа «Всего». Клетка —
 * кнопка: левый клик ставит или снимает «выходит» одной командой, правый клик (клавиша ContextMenu, Shift+F10, долгое нажатие пальцем)
 * открывает меню статусов. «Выходит» заливает клетку целиком цветом кассира с буквой «Р», «выходной день» — холодный тон со штриховкой и «В»,
 * «не выходит» — серая клетка с «Н», не отмеченная — с приглушённой «Н»; загрузчик видно до ответа службы; не отмеченный день, занятый
 * на другом объекте, выглядит как «Н», замок и подпись «занято: …» — при наведении. Выходные
 * столбцы едва заметно затемнены, сегодняшний — синий в шапке, со словом и `aria-current`. Стрелки ходят между клетками, Пробел и Enter переключают. Первый столбец и шапка
 * остаются на месте при прокрутке; прокручиваются только колонки дат (первый столбец закреплён слева, «Всего» справа), ничто не переносится. Нижних строк-итогов нет.
 */
export function ScheduleGrid({ caption, cornerLabel, days, rows, editable, refreshing, scrollKey, rowTools, denied, onToggle, onSetKind, onDismissDenied }: ScheduleGridProps) {
  const scroller = useRef<HTMLDivElement>(null)
  const tip = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState({ row: 0, col: 0 })
  const activeRow = Math.min(active.row, Math.max(rows.length - 1, 0))
  const activeCol = Math.min(active.col, Math.max(days.length - 1, 0))
  const [menu, setMenu] = useState<MenuState | null>(null)
  const press = useRef<{ timer: number; x: number; y: number } | null>(null)
  const longPressed = useRef(false)

  const menuRow = menu === null ? undefined : rows.find((row) => row.key === menu.rowKey)
  const menuCell = menu === null ? undefined : menuRow?.cells.find((cell) => cell.day.day === menu.day)

  useLayoutEffect(() => {
    if (scroller.current !== null) scrollToToday(scroller.current)
  }, [scrollKey])

  const deniedSeq = denied?.seq
  const deniedKey = denied?.key
  useLayoutEffect(() => {
    if (deniedKey === undefined || scroller.current === null || tip.current === null) return
    const root = scroller.current
    const tipElement = tip.current
    const place = () => {
      const cell = cellElement(root, deniedKey)
      if (cell !== undefined) placeTip(tipElement, cell)
    }
    place()
    root.addEventListener('scroll', place)
    window.addEventListener('resize', place)
    return () => {
      root.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)
    }
  }, [deniedKey, deniedSeq])

  useEffect(() => {
    if (deniedKey === undefined) return
    const outside = (event: PointerEvent) => {
      const cell = scroller.current === null ? undefined : cellElement(scroller.current, deniedKey)
      if (!(event.target instanceof Node) || cell?.contains(event.target) !== true) onDismissDenied()
    }
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onDismissDenied()
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [deniedKey, onDismissDenied])

  /** Меню ставится у точки; повторное открытие той же клетки (клавиша и затем событие `contextmenu`) ничего не двигает. */
  const openMenu = (row: GridRow, cell: GridCell, x: number, y: number) => {
    if (cell.saving) return
    setMenu((current) => (current?.rowKey === row.key && current.day === cell.day.day ? current : { rowKey: row.key, day: cell.day.day, x, y }))
  }

  const closeMenu = (refocus: boolean) => {
    setMenu(null)
    if (refocus && scroller.current !== null && menuRow !== undefined && menuCell !== undefined) {
      cellElement(scroller.current, cellKey(menuRow.placeId, menuRow.cashierId, menuCell.day.day))?.focus()
    }
  }

  const chooseKind = (kind: CellKind) => {
    if (menuRow === undefined || menuCell === undefined) return
    closeMenu(true)
    onSetKind(menuRow, menuCell, kind)
  }

  const endPress = () => {
    if (press.current === null) return
    window.clearTimeout(press.current.timer)
    press.current = null
  }

  // Таймер долгого нажатия не переживает таблицу: после размонтирования меню уже никто не откроет.
  useEffect(
    () => () => {
      if (press.current !== null) window.clearTimeout(press.current.timer)
    },
    [],
  )

  const startPress = (event: ReactPointerEvent<HTMLButtonElement>, row: GridRow, cell: GridCell) => {
    // Новое нажатие начинает жест заново: отметка «следующий click подавить» от прежнего долгого нажатия не должна съесть чужой клик.
    longPressed.current = false
    if (event.pointerType !== 'touch') return
    endPress()
    const { clientX: x, clientY: y } = event
    press.current = {
      x,
      y,
      timer: window.setTimeout(() => {
        press.current = null
        longPressed.current = true
        openMenu(row, cell, x, y)
      }, LONG_PRESS_MS),
    }
  }

  const movePress = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = press.current
    if (start !== null && Math.hypot(event.clientX - start.x, event.clientY - start.y) > PRESS_SLOP) endPress()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const target = event.target
    if (!(target instanceof HTMLElement) || target.dataset.row === undefined || target.dataset.col === undefined) return
    if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
      const row = rows[Number(target.dataset.row)]
      const cell = row?.cells[Number(target.dataset.col)]
      if (row === undefined || cell === undefined) return
      event.preventDefault()
      const rect = target.getBoundingClientRect()
      openMenu(row, cell, rect.left, rect.bottom)
      return
    }
    const move = MOVES[event.key]
    if (move === undefined) return
    const next = event.currentTarget.querySelector<HTMLElement>(`[data-row="${Number(target.dataset.row) + move[0]}"][data-col="${Number(target.dataset.col) + move[1]}"]`)
    if (next === null) return
    event.preventDefault()
    next.focus()
  }

  return (
    <div className="sch-tablebox">
      <div ref={scroller} className="sch-scroll" role="region" aria-label={caption} tabIndex={0} aria-busy={refreshing || undefined}>
        <ActionInfoQuiet>
          <table className="sch-tbl" onKeyDown={onKeyDown}>
            <caption className="sch-sr">{caption}</caption>
            <thead>
              <tr>
                <th scope="col" className="sch-tbl__who">
                  {cornerLabel}
                </th>
                {days.map((day) => (
                  <th scope="col" key={day.day} className="sch-tbl__day" data-weekend={day.weekend || undefined} data-today={day.today || undefined} aria-current={day.today ? 'date' : undefined} title={`${day.weekdayShort}, ${formatDayFull(day.day)}`}>
                    <strong>{day.dayNumber}</strong>
                    <span className="sch-tbl__weekday">{day.weekdayShort}</span>
                    {day.today && <small>сег.</small>}
                    <span className="sch-sr">{day.today ? ', сегодня' : day.weekend ? ', выходной день' : ''}</span>
                  </th>
                ))}
                <th scope="col" className="sch-tbl__total">
                  Всего
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.key}>
                  <th scope="row" className="sch-tbl__who">
                    <span className="sch-grid__name">{row.title}</span>
                    {rowTools?.(row)}
                  </th>
                  {row.cells.map((cell, colIndex) => (
                    <td key={cell.day.day} className="sch-tbl__cell" aria-busy={cell.saving || undefined} data-weekend={cell.day.weekend || undefined} data-today={cell.day.today || undefined}>
                      {editable ? (
                        <Button
                          variant="ghost"
                          actionId="schedule.grid.toggle-cell"
                          aria-label={cell.label}
                          title={cell.kind === 'none' && cell.busyAt !== null ? `занято: ${cell.busyAt}` : undefined}
                          data-kind={cell.kind}
                          data-saving={cell.saving || undefined}
                          data-busy={(cell.kind === 'none' && cell.busyAt !== null) || undefined}
                          data-cell={cellKey(row.placeId, row.cashierId, cell.day.day)}
                          data-denied={deniedMark(denied, cellKey(row.placeId, row.cashierId, cell.day.day))}
                          data-swatch={cell.kind === 'work' ? '' : undefined}
                          data-row={rowIndex}
                          data-col={colIndex}
                          tabIndex={rowIndex === activeRow && colIndex === activeCol ? 0 : -1}
                          style={cell.kind === 'work' ? swatchStyle(row.swatch) : undefined}
                          onFocus={() => setActive({ row: rowIndex, col: colIndex })}
                          onClick={() => {
                            if (longPressed.current) longPressed.current = false
                            else onToggle(row, cell)
                          }}
                          onContextMenu={(event) => {
                            event.preventDefault()
                            // Телефон сам шлёт contextmenu при долгом нажатии: жест уже занят меню, click после него не нужен.
                            if (press.current !== null) {
                              endPress()
                              longPressed.current = true
                            }
                            openMenu(row, cell, event.clientX, event.clientY)
                          }}
                          onPointerDown={(event) => startPress(event, row, cell)}
                          onPointerMove={movePress}
                          onPointerUp={endPress}
                          onPointerCancel={endPress}
                          onPointerLeave={endPress}
                          onMouseLeave={() => dismissOn(denied, cellKey(row.placeId, row.cashierId, cell.day.day), onDismissDenied)}
                          onBlur={() => dismissOn(denied, cellKey(row.placeId, row.cashierId, cell.day.day), onDismissDenied)}
                        >
                          <CellMark cell={cell} />
                        </Button>
                      ) : (
                        <span
                          className={cell.kind === 'work' ? 'sch-tbl__mark sch-swatch' : 'sch-tbl__mark'}
                          role="img"
                          aria-label={cell.label}
                          data-kind={cell.kind}
                          data-busy={(cell.kind === 'none' && cell.busyAt !== null) || undefined}
                          style={cell.kind === 'work' ? swatchStyle(row.swatch) : undefined}
                        >
                          <CellMark cell={cell} />
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="sch-tbl__total">{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ActionInfoQuiet>
      </div>
      <div ref={tip} className="sch-denied" role="status" aria-live="polite">
        {denied?.text}
      </div>
      {editable && menu !== null && menuRow !== undefined && menuCell !== undefined && (
        <CellMenu
          title={`Статус клетки: ${menuRow.title}, ${menuCell.day.weekdayShort}, ${formatDayFull(menuCell.day.day)}`}
          kind={menuCell.kind}
          x={menu.x}
          y={menu.y}
          scroller={scroller}
          onChoose={chooseKind}
          onClose={closeMenu}
        />
      )}
    </div>
  )
}
